import React, { createContext, useMemo } from 'react';

/**
 * Verification shape returned by getFinding (core) / getFindingById tool.
 * Matches service-pleri finding core.ts verification mapping.
 */
export interface FactCheckVerification {
  id?: string;
  checkedAt?: string;
  factuality?: string;
  certainty?: string;
  reason?: string;
  statement?: string;
  proof?: {
    environment?: string;
    actions?: Array<{
      description?: string;
      command: string;
      output: { format?: string; value: string };
    }>;
  };
}

export interface FactCheckInfo {
  verification: FactCheckVerification;
  findingStatus?: string;
}

export interface FactCheckLookupValue {
  getVerificationForFindingId: (findingId: string) => FactCheckInfo | null;
}

const defaultLookup: FactCheckLookupValue = {
  getVerificationForFindingId: () => null,
};

export const FactCheckLookupContext =
  createContext<FactCheckLookupValue>(defaultLookup);

export function buildFactCheckLookupFromToolResults(
  content: Array<{ type: string; toolName?: string; content?: unknown }>,
): Map<string, FactCheckInfo> {
  const map = new Map<string, FactCheckInfo>();
  for (const block of content) {
    if (block.type !== 'toolResult' || block.toolName !== 'getFindingById') {
      continue;
    }
    let raw: unknown;
    if (typeof block.content === 'string') {
      try {
        raw = JSON.parse(block.content);
      } catch {
        continue;
      }
    } else {
      raw = block.content;
    }
    if (!raw || typeof raw !== 'object') {
      continue;
    }
    const data = (
      raw as {
        data?: {
          id?: string;
          status?: string;
          verification?: FactCheckVerification;
        };
      }
    ).data;
    const id = data?.id;
    const verification = data?.verification;
    const findingStatus =
      typeof data?.status === 'string' ? data.status.trim() : undefined;
    if (
      typeof id === 'string' &&
      id.trim() !== '' &&
      verification &&
      typeof verification === 'object'
    ) {
      map.set(id, { verification, findingStatus });
    }
  }
  return map;
}

interface FactCheckLookupProviderProps {
  lookup: Map<string, FactCheckInfo>;
  children: React.ReactNode;
}

export const FactCheckLookupProvider: React.FC<
  FactCheckLookupProviderProps
> = ({ lookup, children }) => {
  const value = useMemo<FactCheckLookupValue>(
    () => ({
      getVerificationForFindingId: (findingId: string) =>
        lookup.get(findingId) ?? null,
    }),
    [lookup],
  );
  return (
    <FactCheckLookupContext.Provider value={value}>
      {children}
    </FactCheckLookupContext.Provider>
  );
};
